import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import ServiceCard from "@/components/ServiceCard";
import CTASection from "@/components/CTASection";
import { SERVICES } from "@/data/site";

export default function Services() {
  const [active, setActive] = useState(null);
  const Selected = active?.icon;

  return (
    <div data-testid="page-services">
      <section className="bg-white">
        <div className="sozos-container pt-12 md:pt-16 pb-12">
          <p className="eyebrow">Services</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#0A1F44] mt-4 tracking-tight leading-[1.02] max-w-4xl">
            Nine services. One integrated revenue engine.
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mt-7 max-w-3xl leading-relaxed">
            Engage any service standalone — or stack them into a complete outbound, GTM, and RevOps
            program tuned to your stage and category.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="sozos-container pb-20 md:pb-28">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.slug} service={s} index={i} onClick={() => setActive(s)} />
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="service-detail-modal"
            className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-[#0A1F44]/60 backdrop-blur-sm" onClick={() => setActive(null)} />
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative bg-white max-w-2xl w-full rounded-md shadow-2xl p-8 md:p-10 border border-slate-200"
            >
              <button
                data-testid="close-service-modal"
                onClick={() => setActive(null)}
                className="absolute top-5 right-5 text-slate-400 hover:text-[#0A1F44]"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-12 h-12 bg-[#0A1F44] text-white rounded-sm flex items-center justify-center">
                {Selected && <Selected className="w-6 h-6" />}
              </div>
              <h3 className="font-display text-3xl text-[#0A1F44] mt-5 tracking-tight">{active.title}</h3>
              <p className="text-slate-600 mt-4 leading-relaxed">{active.long}</p>
              <div className="mt-6">
                <p className="eyebrow">What you get</p>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {active.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#1E40AF]" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <a href="/contact#book" data-testid="modal-cta-book" className="btn-primary mt-8">
                Book a Growth Consultation
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="sozos-container section-pad">
          <SectionHeader eyebrow="How we engage" title="Three flexible models — pick what fits." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Pilot", desc: "30–60 day proof-of-value engagement with measurable pipeline KPIs.", note: "Best for first-time partners." },
              { name: "Dedicated Pod", desc: "An always-on team of SDRs, marketers, and ops embedded into your stack.", note: "Best for scaling outbound." },
              { name: "GTM Transformation", desc: "Full-stack revenue overhaul across positioning, motion, and systems.", note: "Best for repositioning or expansion." },
            ].map((m, i) => (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`engagement-${i}`}
                className="bg-white border border-slate-200 rounded-md p-7 card-hover"
              >
                <p className="eyebrow">Model {String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-display text-2xl text-[#0A1F44] mt-3 tracking-tight">{m.name}</h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{m.desc}</p>
                <div className="mt-5 pt-5 border-t border-slate-100 text-xs uppercase tracking-widest text-[#1E40AF] font-semibold">
                  {m.note}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection title="Pick the services. We'll build the engine." subtitle="Get started" />
    </div>
  );
}
